/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { IEditorInput } from '@components/common/TextEditor/useTextEditor'
import {
  CSS_CLASSES,
  HYPERLINK_HTML,
  PLACEHOLDER_URL,
  TEMPLATE_EMAIL_ELEMENT_BUTTONS,
  TEMPLATE_EMAIL_SNIPPETS,
  wrapEmailBlockInRow,
  TEMPLATE_SMS_ELEMENT_BUTTONS,
  URL_REGEX,
} from '@constants/Marketing'
import {
  applyLinkedFeatureIconColors,
  clearLinkedFeatureIconColors,
  FEATURE_ROW_ICON_CHOICES,
  FEATURE_ROW_ICON_DEFAULT_HEX,
  type FeatureRowIconChoice,
  readFeatureRowIconHex,
} from '@constants/Marketing/featureRowIcons'
import {
  CampaignTemplateChannel,
  CampaignTemplateType,
  ICampaignTemplate,
  IElementButton,
  IEmailEditorCanvas,
} from '@dataTypes/marketing.type'
import { setTemplate, setTemplateError } from '@store/actions'
import { setTemplateEditorCanvasPreview } from '@store/reducers/marketing.reducer'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  downscaleDataUrlForEmailInlineImage,
  downscaleDataUrlForEmailLogo,
} from '@utils/marketingEmailImage.util'
import { normalizeBrowserSelectionInRteEditPanel } from '@utils/rteEmailSelection.util'
import {
  getEmailCtaLabel,
  getEmailTextLinkLabel,
  setEmailCtaLabelInHtml,
  setEmailTextLinkLabelInHtml,
} from '@utils/marketingCtaLabels.util'
import {
  isEmailBodyEffectivelyEmpty,
  wrapMockEmailTemplateSectionsInRows,
} from '@utils/marketingTemplate.util'
import {
  ChangeEventArgs,
  MentionComponent,
} from '@syncfusion/ej2-react-dropdowns'
import { RichTextEditorComponent } from '@syncfusion/ej2-react-richtexteditor'
import useEmailRowToolbar from './useEmailRowToolbar'
import {
  ChangeEvent,
  CSSProperties,
  Dispatch,
  MutableRefObject,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { logger } from '@utils/safeLogger'
function toCssSize(value: string, fallback: string): string {
  const t = value.trim()
  if (!t) return fallback
  if (/^\d+(\.\d+)?$/.test(t)) return `${t}px`
  if (/^\d+(\.\d+)?(px|%|rem|em|vh|vw)$/i.test(t)) return t
  return t
}

const DEFAULT_EMAIL_TEMPLATE_ZOOM = '100%'
const DEFAULT_SMS_TEMPLATE_ZOOM = '150%'

/** Reject before FileReader to avoid huge buffers (logo path also downscales pixels). */
const EMAIL_IMAGE_UPLOAD_MAX_BYTES = 12 * 1024 * 1024

function markEmailTemplateCtaAnchorsNonEditable(root: ParentNode): void {
  root
    .querySelectorAll<HTMLAnchorElement>(
      'a.e-rte-email-btn, a.mt-email-tpl__btn',
    )
    .forEach((a) => {
      a.setAttribute('contenteditable', 'false')
    })
}

/** Fallback header strip color when `email_editor_canvas.header_band_color` is unset. */
function defaultHeaderBandColorFromHtml(html: string): string {
  if (!html) return '#ddf1fa'
  if (html.includes('mt-email-tpl__thank-you-header')) return '#d1fae5'
  if (html.includes('mt-email-tpl__recall-header')) return '#ddf1fa'
  if (html.includes('mt-email-tpl__email-header-band')) return '#e0e7ff'
  if (html.includes('mt-email-tpl__email-hero-starter')) return '#3b82f6'
  return '#ddf1fa'
}

/**
 * Primary CTA fill when `email_editor_canvas.cta_button_bg_color` is unset.
 * Keep in sync with `MarketingEmailTemplate.css` `var(--mt-email-cta-btn-bg, …)` fallbacks
 * and Elements button snippet (`#3b82f6`). `--blue-500` / `--teal-500` from `App.css`.
 */
function defaultPrimaryCtaFillFromHtml(html: string): string {
  if (!html) return '#3b82f6'
  if (html.includes('mt-email-tpl__btn--welcome-cta')) return '#0369a1'
  if (html.includes('mt-email-tpl__content--birthday-shell')) return '#78350f'
  if (html.includes('mt-email-tpl__btn--year-end-cta')) return '#0d9488'
  if (html.includes('mt-email-tpl__btn--recall-primary')) {
    if (html.includes('mt-email-tpl--thank-you')) return '#059669'
    return '#185dd8'
  }
  if (html.includes('mt-email-tpl__btn--contact-teal')) return '#24a1a3'
  if (
    html.includes('mt-email-tpl--newsletter') &&
    html.includes('mt-email-tpl__btn')
  ) {
    return '#0284c7'
  }
  if (html.includes('mt-email-tpl__btn--outreach-primary')) return '#185dd8'
  if (html.includes('e-rte-email-btn')) return '#3b82f6'
  if (html.includes('mt-email-tpl__btn')) return '#185dd8'
  return '#3b82f6'
}

/**
 * Only read/update the RTE caret when focus is still in the edit surface.
 * After focus moves to the sidebar, getRange/selection often collapses to the
 * start of the document — overwriting a good saved caret and causing inserts at the top.
 */
function isActiveElementInsideRteEditPanel(
  rte: RichTextEditorComponent | null | undefined,
): boolean {
  if (!rte?.contentModule?.getEditPanel) return false
  const editPanel = rte.contentModule.getEditPanel() as HTMLElement | undefined
  if (!editPanel) return false
  const active = editPanel.ownerDocument.activeElement
  if (!active) return false
  return editPanel === active || editPanel.contains(active)
}

interface TemplatePreviewHooks {
  activeAnchorId: string
  content: IEditorInput
  elementButtons: IElementButton[]
  handleChangeLinkAddress: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleChangeTemplateName: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleChangeZoom: (args: ChangeEventArgs) => void
  handleElementButtonClick: (button: IElementButton) => void
  /** Call on mousedown of Elements (before focus leaves the RTE). */
  snapshotCaretBeforeSidebarClick: () => void
  handleUndoRedo: (action: string) => void
  hyperlinkError: string | null
  isEditing: boolean
  linkAddress: string
  linkStatus: 'Edit' | 'Confirm'
  selectedTemplate: ICampaignTemplate | null
  selectionRef: MutableRefObject<Range | null>
  setContent: Dispatch<SetStateAction<IEditorInput>>
  showHyperlinkSettings: boolean
  templateError: string | null
  templateName: string
  pageFontSize: string
  pageWidth: string
  pageHeight: string
  pageColor: string
  /** Shown when HTML includes recall or thank-you header strip. */
  headerBandApplicable: boolean
  /** User override only; empty means “use template default” in canvas. */
  headerBandColor: string
  /** Hex for the color control (override or layout default). */
  headerBandSwatchValue: string
  pageSurfaceStyle: CSSProperties
  handleChangePageWidth: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleChangePageHeight: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleChangePageColor: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleChangeHeaderBandColor: (e: React.ChangeEvent<HTMLInputElement>) => void
  resetHeaderBandColorToDefault: () => void
  ctaButtonApplicable: boolean
  ctaButtonBgColor: string
  ctaButtonSwatchValue: string
  handleChangeCtaButtonBgColor: (e: React.ChangeEvent<HTMLInputElement>) => void
  resetCtaButtonBgColorToDefault: () => void
  ctaButtonTextColor: string
  ctaButtonTextSwatchValue: string
  handleChangeCtaButtonTextColor: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void
  resetCtaButtonTextColorToDefault: () => void
  ctaButtonAltBgColor: string
  ctaButtonAltSwatchValue: string
  handleChangeCtaButtonAltBgColor: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void
  resetCtaButtonAltBgColorToDefault: () => void
  ctaButtonAltTextColor: string
  ctaButtonAltTextSwatchValue: string
  handleChangeCtaButtonAltTextColor: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void
  resetCtaButtonAltTextColorToDefault: () => void
  /** Page Settings: second color row (Button 2 in body or saved alt color). */
  ctaPageSettingsShowButton2: boolean
  /** Current label text for Page Settings → Button 1 / 2 (from HTML). */
  ctaButtonLabelSlot1: string
  ctaButtonLabelSlot2: string
  handleChangeCtaButtonLabelSlot1: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void
  handleChangeCtaButtonLabelSlot2: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void
  textLinkApplicable: boolean
  textLinkLabel: string
  handleChangeTextLinkLabel: (e: React.ChangeEvent<HTMLInputElement>) => void
  /** Shown when HTML includes Elements Hero block. */
  heroCopyBandApplicable: boolean
  heroCopyBandColor: string
  heroCopyBandSwatchValue: string
  handleChangeHeroCopyBandColor: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void
  resetHeroCopyBandColorToDefault: () => void
  handleChangePageFontSize: (value: string) => void
  toggleLinkStatus: () => void
  toolbarRef: RefObject<RichTextEditorComponent>
  variableRef: RefObject<MentionComponent>
  zoom: string
  previewContainerRef: RefObject<HTMLDivElement>
  canUndo: boolean
  canRedo: boolean
  isEmailTemplate: boolean
  isEmailCanvasEmpty: boolean
  smsEmojiPickerOpen: boolean
  handleSmsEmojiSelect: (emoji: string) => void
  emailLayoutPickerOpen: boolean
  closeEmailLayoutPicker: () => void
  applyEmailStarterLayout: (html: string) => void
  applyEmailSectionBlock: () => void
  emailImageInputRef: RefObject<HTMLInputElement | null>
  handleEmailImageFileChange: (e: ChangeEvent<HTMLInputElement>) => void
  rowHoverToolbar: {
    visible: boolean
    top: number
    left: number
    canMoveUp: boolean
    canMoveDown: boolean
  }
  moveHoveredRow: (direction: 'up' | 'down') => void
  deleteHoveredRow: () => void
  clearRowHoverToolbar: () => void
  lockRowHoverToolbar: () => void
  unlockRowHoverToolbar: () => void
  /** Fixed viewport position for the feature-row icon picker, or null when closed. */
  featureIconPickerPosition: { top: number; left: number } | null
  featureRowIconChoices: readonly FeatureRowIconChoice[]
  applyFeatureRowIcon: (svg: string) => void
  closeFeatureIconPicker: () => void
  /** Hex for the feature icon color picker (icon stroke + border stay linked). */
  featureIconPickerColor: string
  applyFeatureRowIconColor: (hex: string) => void
  resetFeatureRowIconColor: () => void
}

const useTemplatePreview = (): TemplatePreviewHooks => {
  const dispatch = useAppDispatch()
  const selectedTemplate = useAppSelector(
    (state) => state.marketing.selectedTemplate,
  )
  const currentTemplate = useAppSelector(
    (state) => state.marketing.currentTemplate,
  )
  const templateError = useAppSelector(
    (state) => state.marketing.error.template,
  )
  const hyperlinkError = useAppSelector(
    (state) => state.marketing.error.hyperlink,
  )
  const templatePreviewPulse = useAppSelector(
    (state) => state.marketing.templatePreviewPulse,
  )
  const templateEditorResumePulse = useAppSelector(
    (state) => state.marketing.templateEditorResumePulse,
  )
  const lastTemplatePreviewPulseRef = useRef(templatePreviewPulse)
  const lastTemplateEditorResumePulseRef = useRef(templateEditorResumePulse)
  /** Email-only UI (layout blocks, page size/color, canvas surface). SMS stays on the legacy editor shell. */
  const isEmailTemplate = useMemo(() => {
    const channel =
      currentTemplate?.channel ?? selectedTemplate?.channel ?? null
    return channel === CampaignTemplateChannel.email
  }, [currentTemplate?.channel, selectedTemplate?.channel])

  const [elementButtons, setElementButtons] = useState<IElementButton[]>(() => {
    const base = isEmailTemplate
      ? TEMPLATE_EMAIL_ELEMENT_BUTTONS
      : TEMPLATE_SMS_ELEMENT_BUTTONS
    return base.map((b) => ({ ...b }))
  })
  const [smsEmojiPickerOpen, setSmsEmojiPickerOpen] = useState(false)
  const [emailLayoutPickerOpen, setEmailLayoutPickerOpen] = useState(false)
  const [featureIconPickerPosition, setFeatureIconPickerPosition] = useState<{
    top: number
    left: number
  } | null>(null)
  const [featureIconPickerColor, setFeatureIconPickerColor] = useState(
    FEATURE_ROW_ICON_DEFAULT_HEX,
  )
  const featureIconPickerTargetRef = useRef<HTMLElement | null>(null)
  const [content, setContent] = useState<IEditorInput>({
    text: selectedTemplate?.text_content || '',
    htmlContent: selectedTemplate?.html_content || '',
  })
  const isEmailCanvasEmpty = useMemo(
    () => isEmailTemplate && isEmailBodyEffectivelyEmpty(content.htmlContent),
    [isEmailTemplate, content.htmlContent],
  )
  const [isEditing, setIsEditing] = useState<boolean>(true)

  useEffect(() => {
    if (
      templateEditorResumePulse !== lastTemplateEditorResumePulseRef.current
    ) {
      lastTemplateEditorResumePulseRef.current = templateEditorResumePulse
      setIsEditing(true)
    }
  }, [templateEditorResumePulse])

  useEffect(() => {
    dispatch(setTemplateEditorCanvasPreview(!isEditing))
  }, [dispatch, isEditing])

  useEffect(() => {
    if (!isEditing) setEmailLayoutPickerOpen(false)
  }, [isEditing])
  const [showHyperlinkSettings, setShowHyperlinkSettings] =
    useState<boolean>(false)
  const [linkAddress, setLinkAddress] = useState<string>(
    selectedTemplate?.url || '',
  )
  const [linkStatus, setLinkStatus] = useState<'Edit' | 'Confirm'>('Edit')
  const [templateName, setTemplateName] = useState<string>('')
  const [pageFontSize, setPageFontSize] = useState<string>('13px')
  const [pageWidth, setPageWidth] = useState<string>('600')
  const [pageHeight, setPageHeight] = useState<string>('')
  const [pageColor, setPageColor] = useState<string>('#ffffff')
  const [headerBandColor, setHeaderBandColor] = useState<string>('')
  const [ctaButtonBgColor, setCtaButtonBgColor] = useState<string>('')
  const [ctaButtonTextColor, setCtaButtonTextColor] = useState<string>('')
  const [ctaButtonAltBgColor, setCtaButtonAltBgColor] = useState<string>('')
  const [ctaButtonAltTextColor, setCtaButtonAltTextColor] = useState<string>('')
  const [heroCopyBandColor, setHeroCopyBandColor] = useState<string>('')
  const [zoom, setZoom] = useState<string>(() =>
    isEmailTemplate ? DEFAULT_EMAIL_TEMPLATE_ZOOM : DEFAULT_SMS_TEMPLATE_ZOOM,
  )

  useEffect(() => {
    setZoom(
      isEmailTemplate ? DEFAULT_EMAIL_TEMPLATE_ZOOM : DEFAULT_SMS_TEMPLATE_ZOOM,
    )
  }, [isEmailTemplate])

  useEffect(() => {
    if (!isEmailTemplate || !selectedTemplate) return
    const canvas = selectedTemplate.email_editor_canvas
    setPageColor(canvas?.page_color ?? '#ffffff')
    setPageWidth(canvas?.page_width ?? '600')
    setPageHeight(canvas?.page_height ?? '')
    setHeaderBandColor(canvas?.header_band_color?.trim() ?? '')
    setCtaButtonBgColor(canvas?.cta_button_bg_color?.trim() ?? '')
    setCtaButtonTextColor(canvas?.cta_button_text_color?.trim() ?? '')
    setCtaButtonAltBgColor(canvas?.cta_button_alt_bg_color?.trim() ?? '')
    setCtaButtonAltTextColor(canvas?.cta_button_alt_text_color?.trim() ?? '')
    setHeroCopyBandColor(canvas?.hero_copy_band_color?.trim() ?? '')
  }, [
    isEmailTemplate,
    selectedTemplate?.campaign_template_id,
    selectedTemplate?.date_time_created_at,
  ])

  const headerBandApplicable = useMemo(() => {
    if (!isEmailTemplate) return false
    const h = content.htmlContent || ''
    return (
      h.includes('mt-email-tpl__recall-header') ||
      h.includes('mt-email-tpl__thank-you-header') ||
      h.includes('mt-email-tpl__email-header-band') ||
      h.includes('mt-email-tpl__email-hero-starter')
    )
  }, [isEmailTemplate, content.htmlContent])

  const effectiveHeaderBandColor = useMemo(() => {
    if (!headerBandApplicable) return ''
    const custom = headerBandColor.trim()
    if (custom) return custom
    return defaultHeaderBandColorFromHtml(content.htmlContent || '')
  }, [headerBandApplicable, headerBandColor, content.htmlContent])

  const headerBandSwatchValue = useMemo(
    () => effectiveHeaderBandColor || '#ddf1fa',
    [effectiveHeaderBandColor],
  )

  const ctaButtonApplicable = useMemo(() => {
    if (!isEmailTemplate) return false
    const h = content.htmlContent || ''
    return (
      h.includes('e-rte-email-btn') ||
      h.includes('mt-email-tpl__btn--welcome-cta') ||
      h.includes('mt-email-tpl__btn--outreach-primary') ||
      h.includes('mt-email-tpl__btn--outreach-secondary') ||
      h.includes('mt-email-tpl__btn--recall-primary') ||
      h.includes('mt-email-tpl__btn--contact-teal') ||
      h.includes('mt-email-tpl__btn--year-end-cta') ||
      (h.includes('mt-email-tpl--newsletter') &&
        h.includes('mt-email-tpl__btn'))
    )
  }, [isEmailTemplate, content.htmlContent])

  const ctaButtonSwatchValue = useMemo(
    () =>
      ctaButtonBgColor.trim()
        ? ctaButtonBgColor.trim()
        : defaultPrimaryCtaFillFromHtml(content.htmlContent || ''),
    [ctaButtonBgColor, content.htmlContent],
  )

  const ctaButtonAltSwatchValue = useMemo(
    () => (ctaButtonAltBgColor.trim() ? ctaButtonAltBgColor.trim() : '#6366f1'),
    [ctaButtonAltBgColor],
  )

  const ctaButtonTextSwatchValue = useMemo(
    () => (ctaButtonTextColor.trim() ? ctaButtonTextColor.trim() : '#ffffff'),
    [ctaButtonTextColor],
  )

  const ctaButtonAltTextSwatchValue = useMemo(
    () =>
      ctaButtonAltTextColor.trim() ? ctaButtonAltTextColor.trim() : '#ffffff',
    [ctaButtonAltTextColor],
  )

  const heroCopyBandApplicable = useMemo(() => {
    if (!isEmailTemplate) return false
    const h = content.htmlContent || ''
    return h.includes('e-rte-email-hero-image')
  }, [isEmailTemplate, content.htmlContent])

  const heroCopyBandSwatchValue = useMemo(
    () => (heroCopyBandColor.trim() ? heroCopyBandColor.trim() : '#0f172a'),
    [heroCopyBandColor],
  )

  /** Only for Elements → second solid (e-rte-email-btn--alt), not outline (outline uses Button 1). */
  const ctaPageSettingsShowButton2 = useMemo(() => {
    if (!ctaButtonApplicable) return false
    const h = content.htmlContent || ''
    if (h.includes('e-rte-email-btn--alt')) return true
    if (h.includes('mt-email-tpl__btn--outreach-secondary')) return true
    if (ctaButtonAltBgColor.trim()) return true
    if (ctaButtonAltTextColor.trim()) return true
    return false
  }, [
    ctaButtonApplicable,
    content.htmlContent,
    ctaButtonAltBgColor,
    ctaButtonAltTextColor,
  ])

  const ctaButtonLabelSlot1 = useMemo(
    () => getEmailCtaLabel(content.htmlContent || '', 1),
    [content.htmlContent],
  )

  const ctaButtonLabelSlot2 = useMemo(
    () => getEmailCtaLabel(content.htmlContent || '', 2),
    [content.htmlContent],
  )

  const textLinkApplicable = useMemo(() => {
    if (!isEmailTemplate) return false
    const h = content.htmlContent || ''
    return h.includes('e-rte-email-text-link-cta')
  }, [isEmailTemplate, content.htmlContent])

  const textLinkLabel = useMemo(
    () => getEmailTextLinkLabel(content.htmlContent || ''),
    [content.htmlContent],
  )

  const [activeAnchorId, setActiveAnchorId] = useState<string>('')
  const [contentHistory, setContentHistory] = useState<IEditorInput[]>([])
  const [historyIndex, setHistoryIndex] = useState<number>(-1)
  const [isUndoRedoAction, setIsUndoRedoAction] = useState<boolean>(false)
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true)

  const toolbarRef = useRef<RichTextEditorComponent>(null)
  const selectionRef = useRef<Range | null>(null)
  const variableRef = useRef<MentionComponent>(null)
  const previewContainerRef = useRef<HTMLDivElement>(null)
  const emailImageInputRef = useRef<HTMLInputElement | null>(null)
  /** Next file from hidden input: body image, logo insert, hero replace, or mock/logo replace. */
  const emailImagePickIntentRef = useRef<
    'image' | 'logo' | 'hero' | 'logoReplace'
  >('image')
  /** Live RTE node to replace when picking a hero image (placeholder or existing img). */
  const heroImageReplaceTargetRef = useRef<HTMLElement | null>(null)
  /** Mock pre-template logo shell, LOGO placeholder row, or existing logo img to update. */
  const mockLogoReplaceTargetRef = useRef<HTMLElement | null>(null)

  const applyHtmlLabelMutation = useCallback(
    (mutateHtml: (html: string) => string): void => {
      setContent((prev) => {
        const html = mutateHtml(prev.htmlContent)
        const rte = toolbarRef.current
        let text = prev.text
        if (rte && isEditing) {
          try {
            rte.value = html
            text = rte.getText()
          } catch {
            text = prev.text
          }
        } else {
          text = html
            .replace(/<[^>]+>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
        }
        return { text, htmlContent: html }
      })
    },
    [isEditing],
  )

  const applyCtaButtonLabel = useCallback(
    (slot: 1 | 2, next: string): void => {
      applyHtmlLabelMutation((html) => setEmailCtaLabelInHtml(html, slot, next))
    },
    [applyHtmlLabelMutation],
  )

  const handleChangeTextLinkLabel = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value
      applyHtmlLabelMutation((html) => setEmailTextLinkLabelInHtml(html, next))
    },
    [applyHtmlLabelMutation],
  )

  const handleChangeCtaButtonLabelSlot1 = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      applyCtaButtonLabel(1, e.target.value)
    },
    [applyCtaButtonLabel],
  )

  const handleChangeCtaButtonLabelSlot2 = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      applyCtaButtonLabel(2, e.target.value)
    },
    [applyCtaButtonLabel],
  )

  useEffect(() => {
    if (templatePreviewPulse !== lastTemplatePreviewPulseRef.current) {
      lastTemplatePreviewPulseRef.current = templatePreviewPulse
      if (toolbarRef.current) {
        try {
          const rte = toolbarRef.current
          setContent({
            text: rte.getText(),
            htmlContent: rte.getHtml(),
          })
        } catch {
          /* RTE may be mid-teardown */
        }
      }
      setIsEditing(false)
    }
  }, [templatePreviewPulse])

  // Helper functions
  const isValidUrl = (url: string): boolean => {
    if (!url || url === PLACEHOLDER_URL) return false
    const urlToValidate = url.startsWith('http') ? url : `https://${url}`
    return URL_REGEX.test(urlToValidate)
  }

  const getDefaultAddress = (address: string): string =>
    address.length > 0 ? address : PLACEHOLDER_URL

  const parseHtml = (html: string): Document | null => {
    try {
      const parser = new DOMParser()
      return parser.parseFromString(html, 'text/html')
    } catch (error) {
      logger.error('Error parsing HTML:', error)
      return null
    }
  }

  const generateLinkId = (): string =>
    `link_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

  const updateAnchorAttributes = (
    anchor: HTMLAnchorElement,
    address: string,
    id?: string,
  ): void => {
    const isUrlValid = isValidUrl(address)
    anchor.setAttribute('href', address)
    anchor.setAttribute('title', address)
    const preserveCtaClasses =
      anchor.classList.contains('e-rte-email-btn') ||
      anchor.classList.contains('mt-email-tpl__btn')
    if (preserveCtaClasses) {
      if (!anchor.classList.contains(CSS_CLASSES.ANCHOR)) {
        anchor.classList.add(CSS_CLASSES.ANCHOR)
      }
      if (isUrlValid) anchor.classList.remove(CSS_CLASSES.DANGER)
      else anchor.classList.add(CSS_CLASSES.DANGER)
    } else {
      const dangerClass = isUrlValid ? '' : ` ${CSS_CLASSES.DANGER}`
      anchor.className = `${CSS_CLASSES.ANCHOR}${dangerClass}`.trim()
    }
    if (id) {
      anchor.setAttribute('id', id)
    }
  }

  const addToHistory = (newContent: IEditorInput): void => {
    if (isUndoRedoAction) return

    setContentHistory((prev) => {
      const newHistory = prev.slice(0, historyIndex + 1)
      newHistory.push(newContent)
      return newHistory
    })
    setHistoryIndex((prev) => prev + 1)
  }

  const canUndo = historyIndex > 0
  const canRedo = historyIndex < contentHistory.length - 1

  const handleChangeTemplateName = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setTemplateName(e.target.value)
  }

  const handleChangePageWidth = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setPageWidth(e.target.value)
  }

  const handleChangePageHeight = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setPageHeight(e.target.value)
  }

  const handleChangePageColor = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setPageColor(e.target.value)
  }

  const handleChangeHeaderBandColor = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setHeaderBandColor(e.target.value)
  }

  const resetHeaderBandColorToDefault = useCallback((): void => {
    setHeaderBandColor('')
  }, [])

  const handleChangeCtaButtonBgColor = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setCtaButtonBgColor(e.target.value)
  }

  const resetCtaButtonBgColorToDefault = useCallback((): void => {
    setCtaButtonBgColor('')
  }, [])

  const handleChangeCtaButtonTextColor = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setCtaButtonTextColor(e.target.value)
  }

  const resetCtaButtonTextColorToDefault = useCallback((): void => {
    setCtaButtonTextColor('')
  }, [])

  const handleChangeCtaButtonAltBgColor = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setCtaButtonAltBgColor(e.target.value)
  }

  const resetCtaButtonAltBgColorToDefault = useCallback((): void => {
    setCtaButtonAltBgColor('')
  }, [])

  const handleChangeCtaButtonAltTextColor = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setCtaButtonAltTextColor(e.target.value)
  }

  const resetCtaButtonAltTextColorToDefault = useCallback((): void => {
    setCtaButtonAltTextColor('')
  }, [])

  const handleChangeHeroCopyBandColor = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setHeroCopyBandColor(e.target.value)
  }

  const resetHeroCopyBandColorToDefault = useCallback((): void => {
    setHeroCopyBandColor('')
  }, [])

  const handleChangePageFontSize = (value: string): void => {
    const t = (value || '13px').trim()
    setPageFontSize(t ? toCssSize(t, '13px') : '13px')
  }

  const pageWidthCss = useMemo(() => {
    const t = pageWidth.trim()
    if (!t) return '600px'
    return toCssSize(pageWidth, '600px')
  }, [pageWidth])

  const pageMinHeightCss = useMemo(() => {
    const t = pageHeight.trim()
    if (!t) return undefined
    return toCssSize(pageHeight, '')
  }, [pageHeight])

  const pageSurfaceStyle = useMemo((): CSSProperties => {
    const pageBg = pageColor.trim() || '#ffffff'
    const style: CSSProperties = {
      width: pageWidthCss,
      ...(pageMinHeightCss ? { minHeight: pageMinHeightCss } : {}),
      backgroundColor: pageBg,
      fontSize: pageFontSize,
    }
    ;(style as Record<string, string>)['--mt-email-page-bg'] = pageBg
    if (headerBandApplicable && headerBandColor.trim()) {
      ;(style as Record<string, string>)['--mt-email-header-band-bg'] =
        headerBandColor.trim()
    }
    if (ctaButtonApplicable && ctaButtonBgColor.trim()) {
      ;(style as Record<string, string>)['--mt-email-cta-btn-bg'] =
        ctaButtonBgColor.trim()
    }
    if (ctaButtonApplicable && ctaButtonAltBgColor.trim()) {
      ;(style as Record<string, string>)['--mt-email-cta-btn-bg-alt'] =
        ctaButtonAltBgColor.trim()
    }
    if (ctaButtonApplicable && ctaButtonTextColor.trim()) {
      ;(style as Record<string, string>)['--mt-email-cta-btn-fg'] =
        ctaButtonTextColor.trim()
    }
    if (ctaButtonApplicable && ctaButtonAltTextColor.trim()) {
      ;(style as Record<string, string>)['--mt-email-cta-btn-alt-fg'] =
        ctaButtonAltTextColor.trim()
    }
    if (heroCopyBandApplicable && heroCopyBandColor.trim()) {
      ;(style as Record<string, string>)['--mt-email-hero-copy-band-bg'] =
        heroCopyBandColor.trim()
    }
    return style
  }, [
    pageWidthCss,
    pageMinHeightCss,
    pageColor,
    pageFontSize,
    headerBandApplicable,
    headerBandColor,
    ctaButtonApplicable,
    ctaButtonBgColor,
    ctaButtonAltBgColor,
    ctaButtonTextColor,
    ctaButtonAltTextColor,
    heroCopyBandApplicable,
    heroCopyBandColor,
  ])

  const processHtmlContentWithUrlValidation = (htmlContent: string): string => {
    const doc = parseHtml(htmlContent)
    if (!doc) return htmlContent

    const anchors = doc.querySelectorAll<HTMLAnchorElement>('a[href]')
    anchors.forEach((anchor) => {
      const href = anchor.getAttribute('href')
      if (!href) return

      const isUrlValid = isValidUrl(href)

      /* Elements CTA links: never replace classes (preview mode used to strip
       * e-rte-email-btn → Page Settings hid button colors after returning to edit). */
      if (anchor.classList.contains('e-rte-email-btn')) {
        if (isUrlValid) anchor.classList.remove(CSS_CLASSES.DANGER)
        else anchor.classList.add(CSS_CLASSES.DANGER)
        anchor.setAttribute('contenteditable', 'false')
        return
      }
      /* Email template buttons: <a class="mt-email-tpl__btn ..."> — keep classes so
       * label + Page Settings button colors survive processHtml. */
      if (anchor.classList.contains('mt-email-tpl__btn')) {
        if (!anchor.classList.contains(CSS_CLASSES.ANCHOR)) {
          anchor.classList.add(CSS_CLASSES.ANCHOR)
        }
        if (isUrlValid) anchor.classList.remove(CSS_CLASSES.DANGER)
        else anchor.classList.add(CSS_CLASSES.DANGER)
        anchor.setAttribute('contenteditable', 'false')
        return
      }

      const dangerClass = isUrlValid ? '' : ` ${CSS_CLASSES.DANGER}`
      anchor.className = `${CSS_CLASSES.ANCHOR}${dangerClass}`.trim()
    })

    return doc.body.innerHTML
  }

  const getHyperlinkHtml = (): string => {
    const doc = parseHtml(HYPERLINK_HTML)
    if (!doc) return HYPERLINK_HTML

    const anchor = doc.querySelector<HTMLAnchorElement>('a')
    if (anchor) {
      const address = getDefaultAddress(linkAddress)
      updateAnchorAttributes(
        anchor,
        address,
        activeAnchorId.length > 0 ? activeAnchorId : undefined,
      )
    }

    return doc.body.innerHTML
  }

  const handleChangeLinkAddress = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newLinkAddress = e.target.value
    setLinkAddress(newLinkAddress)

    setContent((prev) => {
      const doc = parseHtml(prev.htmlContent)
      if (!doc) return prev

      const address = getDefaultAddress(newLinkAddress)

      if (activeAnchorId.length > 0) {
        const anchor = doc.querySelector<HTMLAnchorElement>(
          `a[id="${activeAnchorId}"]`,
        )
        if (anchor) {
          updateAnchorAttributes(anchor, address)
        }
      } else {
        const anchors = doc.querySelectorAll<HTMLAnchorElement>(
          `a.${CSS_CLASSES.ANCHOR}`,
        )
        anchors.forEach((anchor) => updateAnchorAttributes(anchor, address))
      }

      const updatedHtmlContent = doc.body.innerHTML
      return {
        ...prev,
        htmlContent: processHtmlContentWithUrlValidation(updatedHtmlContent),
      }
    })

    if (hyperlinkError) {
      dispatch(setTemplateError({ error: null, type: 'hyperlink' }))
    }
  }

  const addHyperLinkHtml = (): void => {
    const newLinkId = generateLinkId()
    setActiveAnchorId(newLinkId)

    setContent((prev) => {
      const doc = parseHtml(prev.htmlContent)
      if (!doc) return prev

      // Remove all existing anchor tags
      const anchors = doc.querySelectorAll('a')
      anchors.forEach((anchor) => anchor.remove())

      // Create the hyperlink HTML with the new ID
      const hyperlinkDoc = parseHtml(HYPERLINK_HTML)
      if (!hyperlinkDoc) return prev

      const hyperlinkAnchor = hyperlinkDoc.querySelector<HTMLAnchorElement>('a')
      if (hyperlinkAnchor) {
        const address = getDefaultAddress(linkAddress)
        updateAnchorAttributes(hyperlinkAnchor, address, newLinkId)

        // Find the last paragraph and append the hyperlink HTML
        const paragraphs = doc.querySelectorAll('p')
        if (paragraphs.length > 0) {
          const lastParagraph = paragraphs[paragraphs.length - 1]
          const clonedAnchor = hyperlinkAnchor.cloneNode(
            true,
          ) as HTMLAnchorElement
          lastParagraph.appendChild(clonedAnchor)
        }
      }

      return {
        ...prev,
        htmlContent: doc.body.innerHTML,
      }
    })
  }

  const toggleLinkStatus = (): void => {
    if (linkStatus === 'Edit') {
      const doc = parseHtml(content.htmlContent)
      if (!doc) return

      const hasDangerAnchor =
        doc.querySelector(`a.${CSS_CLASSES.DANGER}`) !== null

      if (hasDangerAnchor) {
        if (!linkAddress.trim()) {
          dispatch(
            setTemplateError({
              error: 'Please enter a link address',
              type: 'hyperlink',
            }),
          )
          return
        }

        if (isValidUrl(linkAddress)) {
          dispatch(setTemplateError({ error: null, type: 'hyperlink' }))
          setLinkStatus('Confirm')
        } else {
          dispatch(
            setTemplateError({
              error: 'Please enter a valid URL format',
              type: 'hyperlink',
            }),
          )
        }
      }
    } else {
      dispatch(setTemplateError({ error: null, type: 'hyperlink' }))
      setLinkStatus('Edit')
    }
  }

  const handleChangeZoom = (args: ChangeEventArgs): void => {
    setZoom(args.item.innerText as string)
  }

  const removeDefaultAnchorClick = (): void => {
    if (!previewContainerRef.current) return
    const container = previewContainerRef.current
    /** RTE body may live in an iframe; main `querySelectorAll('a')` misses those links. */
    const roots: Array<Document | Element> = [container]
    const rteIframe = container.querySelector('iframe')
    if (rteIframe?.contentDocument?.body) {
      roots.push(rteIframe.contentDocument)
    }

    const handleAnchorClick = (event: Event): void => {
      const raw = event.target
      if (raw == null) return
      const el =
        raw instanceof Element ? raw : ((raw as Node).parentElement ?? null)
      const link = el?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!link) return

      /* CTA buttons: label + colors come from Page Settings — do not open Hyperlink sidebar. */
      if (
        link.classList.contains('e-rte-email-btn') ||
        link.classList.contains('mt-email-tpl__btn')
      ) {
        event.preventDefault()
        return
      }

      event.preventDefault()
      const href = link.getAttribute('href')
      const id = link.getAttribute('id')
      if (href) setLinkAddress(href === PLACEHOLDER_URL ? '' : href)
      if (id) setActiveAnchorId(id)
      setShowHyperlinkSettings(true)
    }

    roots.forEach((root) => {
      root.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((anchor) => {
        anchor.addEventListener('click', handleAnchorClick)
      })
    })
  }
  useEffect((): (() => void) | void => {
    removeDefaultAnchorClick()
  }, [content.htmlContent])

  const focusRteInstance = (rte: RichTextEditorComponent): void => {
    const r = rte as RichTextEditorComponent & { focusIn?: () => void }
    if (typeof r.focusIn === 'function') {
      r.focusIn()
    }
  }

  /**
   * While typing, only sync when focus is in the editor — avoids replacing the
   * saved caret with a collapsed range at document start when focus moves away.
   */
  const syncEditorSelectionRefWhileFocused = useCallback((): void => {
    const rte = toolbarRef.current
    if (!rte || !isActiveElementInsideRteEditPanel(rte)) return
    const editPanel = rte.contentModule?.getEditPanel?.() as
      | HTMLElement
      | undefined
    if (!editPanel) return
    const doc = editPanel.ownerDocument
    const win = doc.defaultView ?? window
    try {
      const sel = win.getSelection()
      if (sel?.rangeCount) {
        const r = sel.getRangeAt(0)
        if (editPanel.contains(r.commonAncestorContainer)) {
          selectionRef.current = r.cloneRange()
          return
        }
      }
    } catch {
      /* */
    }
    const nodeSelection = rte.formatter?.editorManager?.nodeSelection
    if (!nodeSelection) return
    try {
      const range = nodeSelection.getRange(doc)
      if (range && editPanel.contains(range.commonAncestorContainer)) {
        selectionRef.current = range.cloneRange()
      }
    } catch {
      /* Range may be unavailable while the editor is updating */
    }
  }, [])

  /**
   * Mousedown on the sidebar often runs *after* focus left the iframe, so
   * `isActiveElementInsideRteEditPanel` is false and a gated snapshot saves nothing.
   * Read the iframe selection directly (still valid for part of the event) and
   * fall back to Syncfusion's range — do not require focus inside the edit panel.
   */
  const snapshotCaretBeforeSidebarClick = useCallback((): void => {
    const rte = toolbarRef.current
    if (!rte?.contentModule?.getEditPanel) return
    const editPanel = rte.contentModule.getEditPanel() as HTMLElement
    const doc = editPanel.ownerDocument
    const win = doc.defaultView ?? window
    try {
      const sel = win.getSelection()
      if (sel?.rangeCount) {
        const r = sel.getRangeAt(0)
        if (editPanel.contains(r.commonAncestorContainer)) {
          selectionRef.current = r.cloneRange()
          return
        }
      }
    } catch {
      /* */
    }
    const nodeSelection = rte.formatter?.editorManager?.nodeSelection
    if (!nodeSelection) return
    try {
      const range = nodeSelection.getRange(doc)
      if (range && editPanel.contains(range.commonAncestorContainer)) {
        selectionRef.current = range.cloneRange()
      }
    } catch {
      /* */
    }
  }, [])

  /** Keeps selectionRef aligned while the caret moves (iframe-safe when RTE uses an iframe). */
  useEffect(() => {
    if (!isEditing) return
    let rafId = 0
    let iframeDoc: Document | null = null
    const sync = (): void => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        syncEditorSelectionRefWhileFocused()
      })
    }
    document.addEventListener('selectionchange', sync)
    let attempts = 0
    const poll = window.setInterval(() => {
      attempts += 1
      const rte = toolbarRef.current
      const ep = rte?.contentModule?.getEditPanel?.() as HTMLElement | undefined
      const idoc = ep?.ownerDocument
      if (!idoc || idoc === document || attempts > 80) {
        clearInterval(poll)
        return
      }
      if (idoc !== iframeDoc) {
        iframeDoc?.removeEventListener('selectionchange', sync)
        iframeDoc = idoc
        iframeDoc.addEventListener('selectionchange', sync)
        clearInterval(poll)
      }
    }, 100)
    return () => {
      clearInterval(poll)
      cancelAnimationFrame(rafId)
      document.removeEventListener('selectionchange', sync)
      iframeDoc?.removeEventListener('selectionchange', sync)
    }
  }, [isEditing, syncEditorSelectionRefWhileFocused])

  /** Leaving the RTE for the sidebar often clears iframe selection before click; snapshot during capture. */
  useEffect(() => {
    if (!isEditing) return
    let attachedPanel: HTMLElement | null = null
    const onFocusOut = (): void => {
      snapshotCaretBeforeSidebarClick()
    }
    let attempts = 0
    const pollId = window.setInterval(() => {
      attempts += 1
      const rte = toolbarRef.current
      const panel = rte?.contentModule?.getEditPanel?.() as
        | HTMLElement
        | undefined
      if (!panel) {
        if (attempts > 80) clearInterval(pollId)
        return
      }
      attachedPanel = panel
      panel.addEventListener('focusout', onFocusOut, true)
      clearInterval(pollId)
    }, 50)
    return () => {
      clearInterval(pollId)
      attachedPanel?.removeEventListener('focusout', onFocusOut, true)
    }
  }, [isEditing, snapshotCaretBeforeSidebarClick])

  const restoreCaretPosition = (): void => {
    const rte = toolbarRef.current
    const nodeSelection = rte?.formatter?.editorManager?.nodeSelection
    if (!rte || !nodeSelection) return

    const editorElem = rte.contentModule?.getEditPanel?.() as
      | HTMLElement
      | undefined
    const doc = editorElem?.ownerDocument ?? document
    const win = doc.defaultView ?? window

    if (selectionRef.current) {
      try {
        const saved = selectionRef.current
        const anchor = saved.commonAncestorContainer
        if (!editorElem || !editorElem.contains(anchor)) {
          selectionRef.current = null
        } else {
          focusRteInstance(rte)
          nodeSelection.selectRange(doc, saved)
          return
        }
      } catch {
        selectionRef.current = null
      }
    }

    if (!editorElem) return

    const range = doc.createRange()
    const selection = win.getSelection()

    let lastTextNode: Text | null = null
    const walker = doc.createTreeWalker(editorElem, NodeFilter.SHOW_TEXT)
    while (walker.nextNode()) {
      lastTextNode = walker.currentNode as Text
    }
    if (lastTextNode && lastTextNode.nodeValue) {
      const textLength = lastTextNode.nodeValue.length
      range.setStart(lastTextNode, textLength)
      range.setEnd(lastTextNode, textLength)
    } else {
      range.selectNodeContents(editorElem)
      range.collapse(false)
    }

    focusRteInstance(rte)
    selection?.removeAllRanges()
    selection?.addRange(range)
    editorElem.focus()
  }

  /** Last resort when Syncfusion commands ignore selection (detached ranges, etc.). */
  const insertHtmlViaDomRange = (
    editorElem: HTMLElement,
    doc: Document,
    win: Window,
    html: string,
    explicitRange?: Range | null,
    placeAfterCurrentRow?: boolean,
  ): void => {
    let range: Range | null = null
    if (explicitRange) {
      try {
        if (editorElem.contains(explicitRange.commonAncestorContainer)) {
          range = explicitRange.cloneRange()
        }
      } catch {
        range = null
      }
    }
    if (!range && selectionRef.current) {
      try {
        const r = selectionRef.current
        if (editorElem.contains(r.commonAncestorContainer)) {
          range = r.cloneRange()
        }
      } catch {
        range = null
      }
    }
    if (!range) return

    if (placeAfterCurrentRow) {
      const anchorNode = range.commonAncestorContainer
      const anchorElement =
        anchorNode.nodeType === Node.ELEMENT_NODE
          ? (anchorNode as Element)
          : anchorNode.parentElement
      const currentRow = anchorElement?.closest('.e-rte-email-row')
      if (currentRow && editorElem.contains(currentRow)) {
        range.setStartAfter(currentRow)
        range.collapse(true)
      }
    }

    const holder = doc.createElement('div')
    holder.innerHTML = html.trim()
    range.deleteContents()
    while (holder.firstChild) {
      const n = holder.firstChild
      range.insertNode(n)
      range.setStartAfter(n)
      range.collapse(true)
    }
    const sel = win.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(range)
    editorElem.dispatchEvent(new InputEvent('input', { bubbles: true }))
  }

  /** Inserts HTML at the saved caret using the real document selection (iframe-safe). */
  const insertHtmlIntoEmailRte = (
    rte: RichTextEditorComponent,
    html: string,
    placeAfterCurrentRow = false,
  ): void => {
    const editorElem = rte.contentModule?.getEditPanel?.() as
      | HTMLElement
      | undefined
    if (!editorElem) return
    const doc = editorElem.ownerDocument
    const win = doc.defaultView ?? window

    restoreCaretPosition()
    focusRteInstance(rte)
    editorElem.focus()

    let trustedRange: Range | null =
      selectionRef.current &&
      editorElem.contains(selectionRef.current.commonAncestorContainer)
        ? selectionRef.current.cloneRange()
        : null

    if (!trustedRange) {
      const sel = win.getSelection()
      if (sel?.rangeCount) {
        const r = sel.getRangeAt(0)
        if (editorElem.contains(r.commonAncestorContainer)) {
          trustedRange = r.cloneRange()
        }
      }
    }

    if (trustedRange) {
      try {
        const sel = win.getSelection()
        sel?.removeAllRanges()
        sel?.addRange(trustedRange.cloneRange())
      } catch {
        /* stale range */
      }
      insertHtmlViaDomRange(
        editorElem,
        doc,
        win,
        html,
        trustedRange,
        placeAfterCurrentRow,
      )
      normalizeBrowserSelectionInRteEditPanel(editorElem)
      markEmailTemplateCtaAnchorsNonEditable(editorElem)
      setContent({
        text: rte.getText(),
        htmlContent: rte.getHtml(),
      })
      return
    }

    try {
      if (selectionRef.current) {
        const anchor = selectionRef.current.commonAncestorContainer
        if (editorElem.contains(anchor)) {
          const sel = win.getSelection()
          sel?.removeAllRanges()
          sel?.addRange(selectionRef.current.cloneRange())
        }
      }
    } catch {
      /* stale range */
    }

    let inserted = false
    try {
      if (typeof doc.execCommand === 'function') {
        inserted = doc.execCommand('insertHTML', false, html)
      }
    } catch {
      inserted = false
    }

    if (!inserted) {
      try {
        rte.executeCommand('insertHTML', html)
      } catch {
        insertHtmlViaDomRange(
          editorElem,
          doc,
          win,
          html,
          null,
          placeAfterCurrentRow,
        )
      }
    }
    editorElem.dispatchEvent(new InputEvent('input', { bubbles: true }))
    normalizeBrowserSelectionInRteEditPanel(editorElem)
    markEmailTemplateCtaAnchorsNonEditable(editorElem)
    setContent({
      text: rte.getText(),
      htmlContent: rte.getHtml(),
    })
  }

  const insertEmailSnippet = (
    html: string,
    placeAfterCurrentRow = false,
  ): void => {
    setIsEditing(true)
    if (!toolbarRef.current) {
      // Empty onboarding state: RTE is not mounted yet. Seed content directly.
      const plain = html
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
      setContent((prev) => {
        const nextHtml = isEmailBodyEffectivelyEmpty(prev.htmlContent)
          ? html
          : `${prev.htmlContent}${html}`
        const nextPlain = `${prev.text || ''} ${plain}`.trim()
        return {
          text: nextPlain,
          htmlContent: nextHtml,
        }
      })
      return
    }
    const run = (): void => {
      const rte = toolbarRef.current
      if (!rte) return
      insertHtmlIntoEmailRte(rte, html, placeAfterCurrentRow)
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(run)
    })
  }

  const insertWrappedEmailRowSnippet = (html: string): void => {
    insertEmailSnippet(wrapEmailBlockInRow(html), true)
  }

  const refreshAnchorListenersSoon = (): void => {
    setTimeout(() => {
      removeDefaultAnchorClick()
    }, 10)
  }

  const syncContentFromRte = useCallback((): void => {
    const rte = toolbarRef.current
    if (!rte) return
    setContent({
      text: rte.getText(),
      htmlContent: rte.getHtml(),
    })
  }, [])

  const closeFeatureIconPicker = useCallback((): void => {
    featureIconPickerTargetRef.current = null
    setFeatureIconPickerPosition(null)
  }, [])

  const applyFeatureRowIcon = useCallback(
    (svg: string): void => {
      const el = featureIconPickerTargetRef.current
      if (!el) {
        setFeatureIconPickerPosition(null)
        return
      }
      el.innerHTML = svg
      const hadCustomColor =
        el.hasAttribute('data-feature-icon-color') ||
        !!(el.style.color && el.style.color.trim())
      if (hadCustomColor) {
        applyLinkedFeatureIconColors(el, readFeatureRowIconHex(el))
      }
      const rte = toolbarRef.current
      const editPanel = rte?.contentModule?.getEditPanel?.() as
        | HTMLElement
        | undefined
      editPanel?.dispatchEvent(new InputEvent('input', { bubbles: true }))
      syncContentFromRte()
      featureIconPickerTargetRef.current = null
      setFeatureIconPickerPosition(null)
    },
    [syncContentFromRte],
  )

  const applyFeatureRowIconColor = useCallback(
    (hex: string): void => {
      const el = featureIconPickerTargetRef.current
      if (!el) return
      applyLinkedFeatureIconColors(el, hex)
      setFeatureIconPickerColor(readFeatureRowIconHex(el))
      const rte = toolbarRef.current
      const editPanel = rte?.contentModule?.getEditPanel?.() as
        | HTMLElement
        | undefined
      editPanel?.dispatchEvent(new InputEvent('input', { bubbles: true }))
      syncContentFromRte()
    },
    [syncContentFromRte],
  )

  const resetFeatureRowIconColor = useCallback((): void => {
    const el = featureIconPickerTargetRef.current
    if (!el) return
    clearLinkedFeatureIconColors(el)
    setFeatureIconPickerColor(FEATURE_ROW_ICON_DEFAULT_HEX)
    const rte = toolbarRef.current
    const editPanel = rte?.contentModule?.getEditPanel?.() as
      | HTMLElement
      | undefined
    editPanel?.dispatchEvent(new InputEvent('input', { bubbles: true }))
    syncContentFromRte()
  }, [syncContentFromRte])

  useEffect(() => {
    if (!isEditing) closeFeatureIconPicker()
  }, [isEditing, closeFeatureIconPicker])

  useEffect(() => {
    if (!featureIconPickerPosition) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeFeatureIconPicker()
    }
    const onDown = (e: MouseEvent): void => {
      const t = e.target as HTMLElement | null
      if (!t?.closest) return
      if (t.closest('.mt-tp-feature-icon-picker')) return
      closeFeatureIconPicker()
    }
    const docs = new Set<Document>()
    docs.add(document)
    const rte = toolbarRef.current
    const editPanel = rte?.contentModule?.getEditPanel?.() as
      | HTMLElement
      | undefined
    const owner = editPanel?.ownerDocument
    if (owner) docs.add(owner)

    window.addEventListener('keydown', onKey)
    docs.forEach((d) => d.addEventListener('mousedown', onDown, true))
    return () => {
      window.removeEventListener('keydown', onKey)
      docs.forEach((d) => d.removeEventListener('mousedown', onDown, true))
    }
  }, [featureIconPickerPosition, closeFeatureIconPicker, toolbarRef])

  const {
    rowHoverToolbar,
    moveHoveredRow,
    deleteHoveredRow,
    clearRowHoverToolbar,
    lockRowHoverToolbar,
    unlockRowHoverToolbar,
  } = useEmailRowToolbar({
    toolbarRef,
    isEmailTemplate,
    isEditing,
    contentHtml: content.htmlContent,
    onRowsMutated: syncContentFromRte,
  })

  useEffect(() => {
    if (!isEmailTemplate || !isEditing) {
      closeFeatureIconPicker()
      return
    }
    let panel: HTMLElement | null = null
    let attempts = 0
    const onClickCapture = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null
      if (!target?.closest) return
      if (target.closest('.mt-tp-feature-icon-picker')) return
      const icon = target.closest(
        '.e-rte-email-row .mt-email-tpl__promo-icon',
      ) as HTMLElement | null
      if (!icon || !panel?.contains(icon)) return
      e.preventDefault()
      e.stopPropagation()
      featureIconPickerTargetRef.current = icon
      setFeatureIconPickerColor(readFeatureRowIconHex(icon))
      const rect = icon.getBoundingClientRect()
      let top = rect.bottom + 6
      let left = rect.left
      if (icon.ownerDocument !== document) {
        const iframe = toolbarRef.current?.element?.querySelector(
          'iframe',
        ) as HTMLIFrameElement | null
        if (iframe) {
          const ir = iframe.getBoundingClientRect()
          top = ir.top + rect.bottom + 6
          left = ir.left + rect.left
        }
      }
      setFeatureIconPickerPosition({ top, left })
    }

    const pollId = window.setInterval(() => {
      attempts += 1
      const rte = toolbarRef.current
      const nextPanel = rte?.contentModule?.getEditPanel?.() as
        | HTMLElement
        | undefined
      if (!nextPanel) {
        if (attempts > 80) window.clearInterval(pollId)
        return
      }
      panel = nextPanel
      panel.addEventListener('click', onClickCapture, true)
      window.clearInterval(pollId)
    }, 80)

    return () => {
      window.clearInterval(pollId)
      panel?.removeEventListener('click', onClickCapture, true)
    }
  }, [
    isEmailTemplate,
    isEditing,
    content.htmlContent,
    closeFeatureIconPicker,
    toolbarRef,
  ])

  useEffect(() => {
    if (!isEmailTemplate || !isEditing) return
    let panel: HTMLElement | null = null
    let attempts = 0
    const onHeroClickCapture = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null
      if (!target?.closest) return
      const slot = target.closest(
        '.e-rte-email-hero-image-placeholder, img.e-rte-email-hero-img',
      ) as HTMLElement | null
      if (!slot || !panel?.contains(slot)) return
      e.preventDefault()
      e.stopPropagation()
      heroImageReplaceTargetRef.current = slot
      emailImagePickIntentRef.current = 'hero'
      emailImageInputRef.current?.click()
    }

    const pollId = window.setInterval(() => {
      attempts += 1
      const rte = toolbarRef.current
      const nextPanel = rte?.contentModule?.getEditPanel?.() as
        | HTMLElement
        | undefined
      if (!nextPanel) {
        if (attempts > 80) window.clearInterval(pollId)
        return
      }
      panel = nextPanel
      panel.addEventListener('click', onHeroClickCapture, true)
      window.clearInterval(pollId)
    }, 80)

    return () => {
      window.clearInterval(pollId)
      panel?.removeEventListener('click', onHeroClickCapture, true)
    }
  }, [isEmailTemplate, isEditing, content.htmlContent, toolbarRef])

  useEffect(() => {
    if (!isEmailTemplate || !isEditing) return
    let panel: HTMLElement | null = null
    let attempts = 0

    const onMockLogoClickCapture = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null
      if (!target?.closest) return

      const imgLogo = target.closest(
        'img.e-rte-email-logo-img',
      ) as HTMLImageElement | null
      if (imgLogo && panel?.contains(imgLogo)) {
        e.preventDefault()
        e.stopPropagation()
        mockLogoReplaceTargetRef.current = imgLogo
        emailImagePickIntentRef.current = 'logoReplace'
        emailImageInputRef.current?.click()
        return
      }

      const placeholder = target.closest(
        '.e-rte-email-logo-placeholder',
      ) as HTMLElement | null
      if (placeholder && panel?.contains(placeholder)) {
        // Single-click: allow selecting “LOGO” text for Font color / formatting.
        // Double-click: open file picker (same as mock logo shells on single click).
        if (e.detail < 2) return
        e.preventDefault()
        e.stopPropagation()
        const hostP = placeholder.closest('p')
        mockLogoReplaceTargetRef.current = hostP ?? placeholder
        emailImagePickIntentRef.current = 'logoReplace'
        emailImageInputRef.current?.click()
        return
      }

      const mockShell = target.closest(
        '.mt-email-tpl__welcome-logo, .mt-email-tpl__recall-logo, .mt-email-tpl__year-end-hero',
      ) as HTMLElement | null
      if (!mockShell || !panel?.contains(mockShell)) return

      e.preventDefault()
      e.stopPropagation()
      mockLogoReplaceTargetRef.current = mockShell
      emailImagePickIntentRef.current = 'logoReplace'
      emailImageInputRef.current?.click()
    }

    const pollId = window.setInterval(() => {
      attempts += 1
      const rte = toolbarRef.current
      const nextPanel = rte?.contentModule?.getEditPanel?.() as
        | HTMLElement
        | undefined
      if (!nextPanel) {
        if (attempts > 80) window.clearInterval(pollId)
        return
      }
      panel = nextPanel
      panel.addEventListener('click', onMockLogoClickCapture, true)
      window.clearInterval(pollId)
    }, 80)

    return () => {
      window.clearInterval(pollId)
      panel?.removeEventListener('click', onMockLogoClickCapture, true)
    }
  }, [isEmailTemplate, isEditing, content.htmlContent, toolbarRef])

  /**
   * Keep CTA anchors non-editable in the live RTE (Syncfusion may drop
   * `contenteditable` on round-trip). Block dblclick so word-select / inline edit
   * does not open inside the button label.
   * Capture click on the edit surface so CTA clicks do not bubble to handlers
   * that open the Hyperlink sidebar (see removeDefaultAnchorClick).
   */
  useEffect(() => {
    if (!isEmailTemplate || !isEditing) return
    let panel: HTMLElement | null = null
    let attempts = 0
    let mo: MutationObserver | null = null
    let raf = 0

    const scheduleMark = (): void => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        if (panel) markEmailTemplateCtaAnchorsNonEditable(panel)
      })
    }

    const onCtaPointerCapture = (e: MouseEvent): void => {
      const t = e.target as HTMLElement | null
      if (!t?.closest || !panel) return
      const cta = t.closest(
        'a.e-rte-email-btn, a.mt-email-tpl__btn',
      ) as HTMLAnchorElement | null
      if (!cta || !panel.contains(cta)) return
      e.preventDefault()
      e.stopPropagation()
    }

    const pollId = window.setInterval(() => {
      attempts += 1
      const rte = toolbarRef.current
      const nextPanel = rte?.contentModule?.getEditPanel?.() as
        | HTMLElement
        | undefined
      if (!nextPanel) {
        if (attempts > 80) window.clearInterval(pollId)
        return
      }
      panel = nextPanel
      scheduleMark()
      mo = new MutationObserver(scheduleMark)
      mo.observe(panel, { childList: true, subtree: true })
      panel.addEventListener('click', onCtaPointerCapture, true)
      panel.addEventListener('dblclick', onCtaPointerCapture, true)
      window.clearInterval(pollId)
    }, 80)

    return () => {
      window.clearInterval(pollId)
      cancelAnimationFrame(raf)
      mo?.disconnect()
      panel?.removeEventListener('click', onCtaPointerCapture, true)
      panel?.removeEventListener('dblclick', onCtaPointerCapture, true)
    }
  }, [isEmailTemplate, isEditing, toolbarRef])

  const handleEmailImageFileChange = (
    e: ChangeEvent<HTMLInputElement>,
  ): void => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !isEmailTemplate) return
    if (!file.type.startsWith('image/')) {
      dispatch(
        setTemplateError({
          error:
            'Please choose an image file (PNG, JPEG, WebP, GIF, etc.). Other file types cannot be used here.',
          type: 'template',
        }),
      )
      return
    }

    if (file.size > EMAIL_IMAGE_UPLOAD_MAX_BYTES) {
      dispatch(
        setTemplateError({
          error: `Image is too large (${Math.round(file.size / (1024 * 1024))} MB). Please use a file under ${EMAIL_IMAGE_UPLOAD_MAX_BYTES / (1024 * 1024)} MB.`,
          type: 'template',
        }),
      )
      return
    }

    const intent = emailImagePickIntentRef.current
    emailImagePickIntentRef.current = 'image'
    if (intent !== 'logoReplace') {
      mockLogoReplaceTargetRef.current = null
    }

    const reader = new FileReader()
    reader.onload = (): void => {
      const dataUrl = reader.result as string
      if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image')) {
        return
      }

      const applyEmailImageDataUrl = (finalUrl: string): void => {
        if (intent === 'hero') {
          const el = heroImageReplaceTargetRef.current
          heroImageReplaceTargetRef.current = null
          if (!el?.isConnected) return
          const doc = el.ownerDocument
          const img = doc.createElement('img')
          img.className = 'e-rte-email-hero-img'
          img.setAttribute('src', finalUrl)
          img.setAttribute('alt', '')
          img.setAttribute(
            'style',
            'width:100%;max-width:100%;height:auto;display:block;border:0;vertical-align:middle;',
          )
          img.setAttribute('contenteditable', 'false')
          el.replaceWith(img)
          const rte = toolbarRef.current
          const editPanel = rte?.contentModule?.getEditPanel?.() as
            | HTMLElement
            | undefined
          editPanel?.dispatchEvent(new InputEvent('input', { bubbles: true }))
          normalizeBrowserSelectionInRteEditPanel(editPanel)
          syncContentFromRte()
          return
        }

        if (intent === 'logoReplace') {
          const el = mockLogoReplaceTargetRef.current
          mockLogoReplaceTargetRef.current = null
          if (!el?.isConnected) return
          const rte = toolbarRef.current
          const editPanel = rte?.contentModule?.getEditPanel?.() as
            | HTMLElement
            | undefined

          if (
            el.tagName === 'IMG' &&
            el.classList.contains('e-rte-email-logo-img')
          ) {
            el.setAttribute('src', finalUrl)
            el.setAttribute('alt', 'Logo')
            editPanel?.dispatchEvent(new InputEvent('input', { bubbles: true }))
            normalizeBrowserSelectionInRteEditPanel(editPanel)
            syncContentFromRte()
            return
          }

          const doc = el.ownerDocument
          const isWelcomeShell = el.classList.contains(
            'mt-email-tpl__welcome-logo',
          )
          const isRecallShell = el.classList.contains(
            'mt-email-tpl__recall-logo',
          )
          const isYearEndHero = el.classList.contains(
            'mt-email-tpl__year-end-hero',
          )
          /**
           * Match mock tooth chip footprint in MarketingEmailTemplate.css (circles 32 / 28 / 56px).
           * Use a modest border-radius (not 50%) so rectangular / wordmark logos are not forced
           * into a circular mask; object-fit:contain keeps aspect ratio inside the box.
           */
          const mockLogoImgRadius = '8px'
          let imgStyle: string
          let mockSlot: 'welcome' | 'recall' | 'year-end' | null = null
          if (isYearEndHero) {
            mockSlot = 'year-end'
            imgStyle = `max-width:56px;max-height:56px;width:auto;height:auto;object-fit:contain;border-radius:${mockLogoImgRadius};display:inline-block;vertical-align:middle;`
          } else if (isWelcomeShell) {
            mockSlot = 'welcome'
            imgStyle = `max-width:32px;max-height:32px;width:auto;height:auto;object-fit:contain;border-radius:${mockLogoImgRadius};display:inline-block;vertical-align:middle;`
          } else if (isRecallShell) {
            mockSlot = 'recall'
            imgStyle = `max-width:28px;max-height:28px;width:auto;height:auto;object-fit:contain;border-radius:${mockLogoImgRadius};display:inline-block;vertical-align:middle;`
          } else {
            imgStyle =
              'max-width:min(100%,240px);max-height:72px;width:auto;height:auto;object-fit:contain;border-radius:8px;display:inline-block;'
          }

          const p = doc.createElement('p')
          p.setAttribute(
            'style',
            mockSlot
              ? 'margin:0;text-align:center;'
              : 'margin:10px 0;text-align:center;',
          )
          const logoImg = doc.createElement('img')
          logoImg.className = 'e-rte-email-logo-img'
          if (mockSlot) {
            logoImg.setAttribute('data-mock-logo-slot', mockSlot)
          }
          logoImg.setAttribute('src', finalUrl)
          logoImg.setAttribute('alt', 'Logo')
          logoImg.setAttribute('style', imgStyle)
          logoImg.setAttribute('contenteditable', 'false')
          p.appendChild(logoImg)
          el.replaceWith(p)

          editPanel?.dispatchEvent(new InputEvent('input', { bubbles: true }))
          normalizeBrowserSelectionInRteEditPanel(editPanel)
          syncContentFromRte()
          return
        }

        const isLogo = intent === 'logo'
        const imgStyle = isLogo
          ? 'max-width:min(100%,240px);max-height:72px;width:auto;height:auto;object-fit:contain;border-radius:8px;display:inline-block;'
          : 'max-width:100%;height:auto;border-radius:6px;display:inline-block;'
        const imgClass = isLogo ? ' class="e-rte-email-logo-img"' : ''
        const alt = isLogo ? 'Logo' : ''
        const imgHtml = wrapEmailBlockInRow(
          `<p style="margin:10px 0;text-align:center;"><img${imgClass} src="${finalUrl}" alt="${alt}" style="${imgStyle}" /></p>`,
        )
        insertEmailSnippet(imgHtml, true)
      }

      const optimizePipeline =
        intent === 'logo' || intent === 'logoReplace'
          ? downscaleDataUrlForEmailLogo(dataUrl)
          : intent === 'hero' || intent === 'image'
            ? downscaleDataUrlForEmailInlineImage(dataUrl)
            : Promise.resolve(dataUrl)

      void optimizePipeline.then((finalUrl) => {
        applyEmailImageDataUrl(finalUrl)
      })
    }
    reader.onerror = (): void => {
      console.error('Failed to read image for email template')
    }
    reader.readAsDataURL(file)
  }

  const handleHyperlinkButton = (): void => {
    setShowHyperlinkSettings(true)
    setLinkAddress('')

    const newLinkId = generateLinkId()
    setActiveAnchorId(newLinkId)

    if (isEditing) {
      restoreCaretPosition()

      toolbarRef.current?.executeCommand('insertText', '')
      toolbarRef.current?.executeCommand('insertHTML', getHyperlinkHtml())
    } else {
      const doc = parseHtml(content.htmlContent)
      if (doc && !doc.querySelector('a')) {
        addHyperLinkHtml()
      }
    }
    refreshAnchorListenersSoon()
  }

  const openVariablePicker = (): void => {
    setSmsEmojiPickerOpen(false)
    setIsEditing(true)
    setTimeout(() => {
      restoreCaretPosition()
      const rte = toolbarRef.current
      if (variableRef.current && rte) {
        if (rte.getHtml() === '<p><br></p>') rte.value = '<p>&nbsp;</p>'
        variableRef.current.showPopup()
      }
    }, 10)
  }

  const handleSmsEmojiSelect = (emoji: string): void => {
    setSmsEmojiPickerOpen(false)
    setIsEditing(true)
    setTimeout(() => {
      restoreCaretPosition()
      toolbarRef.current?.executeCommand('insertText', emoji)
    }, 10)
  }

  const pickNextAlternatingEmailButtonSnippet = (): string => {
    let raw = content.htmlContent || ''
    try {
      if (toolbarRef.current) {
        raw = toolbarRef.current.getHtml() || raw
      }
    } catch {
      /* RTE not ready */
    }
    const doc = parseHtml(raw)
    if (!doc) return TEMPLATE_EMAIL_SNIPPETS.button
    const nPrimary = doc.querySelectorAll(
      '.e-rte-email-btn:not(.e-rte-email-btn--alt)',
    ).length
    const nAlt = doc.querySelectorAll('.e-rte-email-btn--alt').length
    return nPrimary <= nAlt
      ? TEMPLATE_EMAIL_SNIPPETS.button
      : TEMPLATE_EMAIL_SNIPPETS.buttonAlt
  }

  const appendInfoTableRowAtCaret = (): boolean => {
    const rte = toolbarRef.current
    const editorElem = rte?.contentModule?.getEditPanel?.() as
      | HTMLElement
      | undefined
    if (!rte || !editorElem) return false

    restoreCaretPosition()

    const doc = editorElem.ownerDocument
    const win = doc.defaultView ?? window
    const sel = win.getSelection()
    const range =
      sel?.rangeCount &&
      editorElem.contains(sel.getRangeAt(0).commonAncestorContainer)
        ? sel.getRangeAt(0)
        : null

    const anchorElement =
      range?.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
        ? (range.commonAncestorContainer as Element)
        : range?.commonAncestorContainer.parentElement
    const table = anchorElement?.closest(
      'table.e-rte-email-info-table',
    ) as HTMLTableElement | null
    if (!table || !editorElem.contains(table)) return false

    const tbody = table.querySelector('tbody')
    if (!tbody) return false

    const rows = Array.from(tbody.querySelectorAll('tr'))
    if (!rows.length) return false

    const previousLastRow = rows[rows.length - 1]
    previousLastRow.querySelectorAll('td').forEach((cell) => {
      ;(cell as HTMLElement).style.borderBottom = '1px solid #f1f5f9'
    })

    const sourceRow = previousLastRow
    const newRow = sourceRow.cloneNode(true) as HTMLTableRowElement
    const cells = Array.from(newRow.querySelectorAll('td')) as HTMLElement[]
    if (cells[0]) {
      cells[0].textContent = 'New label'
      cells[0].style.borderBottom = ''
    }
    if (cells[1]) {
      cells[1].textContent = 'Add value'
      cells[1].style.borderBottom = ''
    }

    tbody.appendChild(newRow)
    editorElem.dispatchEvent(new InputEvent('input', { bubbles: true }))
    syncContentFromRte()
    return true
  }

  const handleElementButtonClick = (button: IElementButton): void => {
    switch (button.id) {
      case 'layout':
        if (isEmailTemplate) {
          setIsEditing(true)
          setEmailLayoutPickerOpen((open) => !open)
        }
        break
      case 'text':
        setSmsEmojiPickerOpen(false)
        if (isEmailTemplate) {
          insertEmailSnippet(TEMPLATE_EMAIL_SNIPPETS.row, true)
        } else {
          setIsEditing(!isEditing)
        }
        break
      case 'columns':
        insertEmailSnippet(TEMPLATE_EMAIL_SNIPPETS.columns, true)
        break
      case 'button':
        insertWrappedEmailRowSnippet(pickNextAlternatingEmailButtonSnippet())
        break
      case 'image':
        if (isEmailTemplate) {
          setSmsEmojiPickerOpen(false)
          setEmailLayoutPickerOpen(false)
          setIsEditing(true)
          emailImagePickIntentRef.current = 'image'
          emailImageInputRef.current?.click()
        }
        break
      case 'logo':
        if (isEmailTemplate) {
          setSmsEmojiPickerOpen(false)
          setEmailLayoutPickerOpen(false)
          setIsEditing(true)
          emailImagePickIntentRef.current = 'logo'
          emailImageInputRef.current?.click()
        }
        break
      case 'divider':
        insertWrappedEmailRowSnippet(TEMPLATE_EMAIL_SNIPPETS.divider)
        break
      case 'headerBand':
        insertWrappedEmailRowSnippet(TEMPLATE_EMAIL_SNIPPETS.headerBand)
        break
      case 'featureRow':
        insertWrappedEmailRowSnippet(TEMPLATE_EMAIL_SNIPPETS.featureRow)
        break
      case 'columns3':
        insertEmailSnippet(TEMPLATE_EMAIL_SNIPPETS.columns3, true)
        break
      case 'columns2575':
        insertEmailSnippet(TEMPLATE_EMAIL_SNIPPETS.columns2575, true)
        break
      case 'socialRow':
        insertEmailSnippet(TEMPLATE_EMAIL_SNIPPETS.socialRow, true)
        break
      case 'footerBlock':
        insertEmailSnippet(TEMPLATE_EMAIL_SNIPPETS.footerBlock, true)
        break
      case 'spacer':
        insertEmailSnippet(TEMPLATE_EMAIL_SNIPPETS.spacer, true)
        break
      case 'quote':
        insertEmailSnippet(TEMPLATE_EMAIL_SNIPPETS.quote, true)
        break
      case 'tableInfo':
        if (!appendInfoTableRowAtCaret()) {
          insertEmailSnippet(TEMPLATE_EMAIL_SNIPPETS.tableInfo, true)
        }
        break
      case 'heroImage':
        insertEmailSnippet(TEMPLATE_EMAIL_SNIPPETS.heroImage, true)
        break
      case 'textLinkCta':
        insertEmailSnippet(TEMPLATE_EMAIL_SNIPPETS.textLinkCta, true)
        break
      case 'buttonOutline':
        insertWrappedEmailRowSnippet(TEMPLATE_EMAIL_SNIPPETS.buttonOutline)
        break
      case 'hyperlink':
        setSmsEmojiPickerOpen(false)
        handleHyperlinkButton()
        break
      case 'variable':
        setSmsEmojiPickerOpen(false)
        setEmailLayoutPickerOpen(false)
        openVariablePicker()
        break
      case 'emoji':
        setIsEditing(true)
        setSmsEmojiPickerOpen((open) => !open)
        break
      default:
        break
    }
  }

  const handleUndoRedo = (action: string): void => {
    if (action === 'undo' && canUndo) {
      setIsUndoRedoAction(true)
      const previousContent = contentHistory[historyIndex - 1]
      setContent(previousContent)
      setHistoryIndex((prev) => prev - 1)
      setTimeout(() => setIsUndoRedoAction(false), 100)
    } else if (action === 'redo' && canRedo) {
      setIsUndoRedoAction(true)
      const nextContent = contentHistory[historyIndex + 1]
      setContent(nextContent)
      setHistoryIndex((prev) => prev + 1)
      setTimeout(() => setIsUndoRedoAction(false), 100)
    }
  }

  useEffect(() => {
    const base = isEmailTemplate
      ? TEMPLATE_EMAIL_ELEMENT_BUTTONS
      : TEMPLATE_SMS_ELEMENT_BUTTONS
    const emailNeedsEdit = [
      'layout',
      'columns',
      'button',
      'image',
      'logo',
      'divider',
      'headerBand',
      'hyperlink',
      'variable',
    ]
    const smsNeedsEdit = ['hyperlink', 'variable', 'emoji']
    const needsEditIds = isEmailTemplate ? emailNeedsEdit : smsNeedsEdit

    setElementButtons(
      base.map((b) => {
        const needsEdit = needsEditIds.includes(b.id)
        const layoutActive =
          b.id === 'layout' && isEmailTemplate && emailLayoutPickerOpen
        const textActive = isEmailTemplate
          ? !isEmailCanvasEmpty && isEditing
          : isEditing
        return {
          ...b,
          active: b.id === 'text' ? textActive : layoutActive,
          disabled:
            b.id === 'text'
              ? false
              : needsEdit
                ? !isEditing && !isEmailCanvasEmpty
                : false,
        }
      }),
    )
  }, [isEmailTemplate, isEditing, emailLayoutPickerOpen, content.htmlContent])

  useEffect(() => {
    if (isEmailTemplate) setSmsEmojiPickerOpen(false)
    else setEmailLayoutPickerOpen(false)
  }, [isEmailTemplate])

  /**
   * Mock marketing emails: wrap major sections in e-rte-email-row so move/up/down/delete works.
   * Runs when the selected template identity changes (preview remount usually resets content too).
   */
  useEffect(() => {
    if (!isEmailTemplate || !selectedTemplate) return
    const raw = selectedTemplate.html_content || ''
    if (!raw.includes('mt-email-tpl')) return
    const wrapped = wrapMockEmailTemplateSectionsInRows(raw)
    setContent({
      text: selectedTemplate.text_content || '',
      htmlContent: wrapped,
    })
  }, [isEmailTemplate, selectedTemplate?.campaign_template_id])

  useEffect(() => {
    if (isEmailTemplate && !isEmailBodyEffectivelyEmpty(content.htmlContent)) {
      setEmailLayoutPickerOpen(false)
    }
  }, [isEmailTemplate, content.htmlContent])

  useEffect(() => {
    if (!isEditing) {
      const processedHtmlContent = processHtmlContentWithUrlValidation(
        content.htmlContent,
      )
      if (processedHtmlContent !== content.htmlContent) {
        setContent((prev) => ({
          ...prev,
          htmlContent: processedHtmlContent,
        }))
      }
    }
  }, [content.htmlContent, isEditing])

  useEffect(() => {
    if (selectedTemplate?.type === CampaignTemplateType.saved) {
      setTemplateName(selectedTemplate.name)
    }
  }, [selectedTemplate])

  useEffect(() => {
    if (isInitialLoad) {
      setIsInitialLoad(false)
    }
  }, [isInitialLoad])

  useEffect(() => {
    if (!isUndoRedoAction && !isInitialLoad) {
      addToHistory(content)
    }
  }, [content, isInitialLoad])

  useEffect(() => {
    const processedHtmlContent = processHtmlContentWithUrlValidation(
      content.htmlContent,
    )

    const emailCanvas: IEmailEditorCanvas | undefined = isEmailTemplate
      ? {
          page_color: pageColor,
          page_width: pageWidth,
          ...(pageHeight.trim() ? { page_height: pageHeight.trim() } : {}),
          ...(headerBandApplicable && headerBandColor.trim()
            ? { header_band_color: headerBandColor.trim() }
            : {}),
          ...(ctaButtonApplicable && ctaButtonBgColor.trim()
            ? { cta_button_bg_color: ctaButtonBgColor.trim() }
            : {}),
          ...(ctaButtonApplicable && ctaButtonAltBgColor.trim()
            ? { cta_button_alt_bg_color: ctaButtonAltBgColor.trim() }
            : {}),
          ...(ctaButtonApplicable && ctaButtonTextColor.trim()
            ? { cta_button_text_color: ctaButtonTextColor.trim() }
            : {}),
          ...(ctaButtonApplicable && ctaButtonAltTextColor.trim()
            ? { cta_button_alt_text_color: ctaButtonAltTextColor.trim() }
            : {}),
          ...(heroCopyBandApplicable && heroCopyBandColor.trim()
            ? { hero_copy_band_color: heroCopyBandColor.trim() }
            : {}),
        }
      : undefined

    const newTemplate: ICampaignTemplate = currentTemplate
      ? {
          ...currentTemplate,
          name: templateName,
          text_content: content.text,
          html_content: processedHtmlContent,
          url: linkAddress.trim().length > 0 ? linkAddress : undefined,
          ...(isEmailTemplate
            ? { email_editor_canvas: emailCanvas }
            : { email_editor_canvas: undefined }),
        }
      : {
          channel: selectedTemplate?.channel ?? CampaignTemplateChannel.sms,
          name: templateName,
          type: CampaignTemplateType.saved,
          text_content: content.text,
          html_content: processedHtmlContent,
          url: linkAddress,
          created_by: '',
          date_time_created_at: new Date().toISOString(),
          date_time_updated_at: new Date().toISOString(),
          ...(isEmailTemplate
            ? { email_editor_canvas: emailCanvas }
            : { email_editor_canvas: undefined }),
        }
    dispatch(
      setTemplate({
        template: newTemplate,
        type: 'current',
      }),
    )
  }, [
    templateName,
    content,
    linkAddress,
    selectedTemplate?.channel,
    isEmailTemplate,
    pageWidth,
    pageHeight,
    pageColor,
    headerBandApplicable,
    headerBandColor,
    ctaButtonApplicable,
    ctaButtonBgColor,
    ctaButtonAltBgColor,
    ctaButtonTextColor,
    ctaButtonAltTextColor,
    heroCopyBandApplicable,
    heroCopyBandColor,
  ])

  useEffect(() => {
    if (linkStatus === 'Confirm') {
      const anchorTag = document.getElementById(activeAnchorId)
      if (anchorTag) {
        updateAnchorAttributes(anchorTag as HTMLAnchorElement, linkAddress)
      }
      setContent((prev) => {
        const doc = parseHtml(prev.htmlContent)
        if (!doc) return prev

        const anchor = doc.querySelector<HTMLAnchorElement>(
          `a[id="${activeAnchorId}"]`,
        )

        if (anchor) {
          updateAnchorAttributes(anchor, linkAddress)
          // Ensure anchor class is present
          if (!anchor.classList.contains(CSS_CLASSES.ANCHOR)) {
            anchor.classList.add(CSS_CLASSES.ANCHOR)
          }
        }

        return {
          ...prev,
          htmlContent: doc.body.innerHTML,
        }
      })
    }
  }, [linkStatus, activeAnchorId, linkAddress])

  const findAnchorId = (): void => {
    const anchorTags = document.querySelectorAll<HTMLAnchorElement>(
      `a.${CSS_CLASSES.DANGER}`,
    )
    if (anchorTags.length > 0) {
      setActiveAnchorId(anchorTags[0].getAttribute('id') || '')
    }
  }

  useEffect(() => {
    if (hyperlinkError) {
      setShowHyperlinkSettings(true)
    } else if (templateError) {
      setLinkAddress('')
      findAnchorId()
      setShowHyperlinkSettings(false)
    }
  }, [hyperlinkError, templateError])

  useEffect(() => {
    toggleLinkStatus()
  }, [linkAddress])

  const closeEmailLayoutPicker = useCallback((): void => {
    setEmailLayoutPickerOpen(false)
  }, [])

  const applyEmailStarterLayout = useCallback((html: string): void => {
    setIsEditing(true)
    setSmsEmojiPickerOpen(false)
    setEmailLayoutPickerOpen(false)
    const plain = html
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    setContent({ text: plain, htmlContent: html })
    refreshAnchorListenersSoon()
  }, [])

  const applyEmailSectionBlock = useCallback((): void => {
    setEmailLayoutPickerOpen(false)
    setSmsEmojiPickerOpen(false)
    insertEmailSnippet(TEMPLATE_EMAIL_SNIPPETS.layout)
    refreshAnchorListenersSoon()
  }, [])

  return {
    activeAnchorId,
    content,
    elementButtons,
    handleChangeLinkAddress,
    handleChangeTemplateName,
    handleChangeZoom,
    handleElementButtonClick,
    snapshotCaretBeforeSidebarClick,
    handleUndoRedo,
    hyperlinkError,
    isEditing,
    linkAddress,
    linkStatus,
    selectedTemplate,
    selectionRef,
    setContent,
    showHyperlinkSettings,
    templateError,
    templateName,
    pageFontSize,
    pageWidth,
    pageHeight,
    pageColor,
    headerBandApplicable,
    headerBandColor,
    headerBandSwatchValue,
    pageSurfaceStyle,
    handleChangePageWidth,
    handleChangePageHeight,
    handleChangePageColor,
    handleChangeHeaderBandColor,
    resetHeaderBandColorToDefault,
    ctaButtonApplicable,
    ctaButtonBgColor,
    ctaButtonSwatchValue,
    handleChangeCtaButtonBgColor,
    resetCtaButtonBgColorToDefault,
    ctaButtonTextColor,
    ctaButtonTextSwatchValue,
    handleChangeCtaButtonTextColor,
    resetCtaButtonTextColorToDefault,
    ctaButtonAltBgColor,
    ctaButtonAltSwatchValue,
    handleChangeCtaButtonAltBgColor,
    resetCtaButtonAltBgColorToDefault,
    ctaButtonAltTextColor,
    ctaButtonAltTextSwatchValue,
    handleChangeCtaButtonAltTextColor,
    resetCtaButtonAltTextColorToDefault,
    ctaPageSettingsShowButton2,
    ctaButtonLabelSlot1,
    ctaButtonLabelSlot2,
    handleChangeCtaButtonLabelSlot1,
    handleChangeCtaButtonLabelSlot2,
    textLinkApplicable,
    textLinkLabel,
    handleChangeTextLinkLabel,
    heroCopyBandApplicable,
    heroCopyBandColor,
    heroCopyBandSwatchValue,
    handleChangeHeroCopyBandColor,
    resetHeroCopyBandColorToDefault,
    handleChangePageFontSize,
    toggleLinkStatus,
    toolbarRef,
    variableRef,
    zoom,
    previewContainerRef,
    canUndo,
    canRedo,
    isEmailTemplate,
    isEmailCanvasEmpty,
    smsEmojiPickerOpen,
    handleSmsEmojiSelect,
    emailLayoutPickerOpen,
    closeEmailLayoutPicker,
    applyEmailStarterLayout,
    applyEmailSectionBlock,
    emailImageInputRef,
    handleEmailImageFileChange,
    rowHoverToolbar,
    moveHoveredRow,
    deleteHoveredRow,
    clearRowHoverToolbar,
    lockRowHoverToolbar,
    unlockRowHoverToolbar,
    featureIconPickerPosition,
    featureRowIconChoices: FEATURE_ROW_ICON_CHOICES,
    applyFeatureRowIcon,
    closeFeatureIconPicker,
    featureIconPickerColor,
    applyFeatureRowIconColor,
    resetFeatureRowIconColor,
  }
}

export default useTemplatePreview
